/**
 * HubSpot Injector (Minimal Debug-first Version)
 *
 * Goal: ensure script boot, session availability and form manipulability,
 * then inject values from SessionStorageManager into HubSpot forms by
 * matching top-level session keys to input[name="<key>"].
 */

import { PipefyLogger } from './utils/logger.js';

(function () {
  'use strict';

  class HubSpotInjector {
    constructor() {
      this.config = window.pipefyHubSpotConfig || {};
      this.trackingConfig = window.pipefyTrackingConfig || {};
      this.logger = PipefyLogger.create('HubSpotInjector');
      this.startedAt = Date.now();
      this.sessionAvailable = false;
      this.hookReady = false;
      this.pollers = [];
      this.init();
    }

    init() {
      this.logger.info('init: starting HubSpotInjector');

      const onReady = () => {
        this.logger.debug('dom: ready');
        this.checkSessionManager();
        this.hookHubSpotCreate();
        this.setupHubSpotMessageListener();
        this.processExistingForms();
        this.watchPhoneMasks();
        this.logger.info('init: completed');
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        onReady();
      }
    }

    // ---------- Logging ----------
    isDebugMode() {
      try {
        // Prefer explicit config flags; fall back to global debug helpers
        return Boolean(
          (this.config && this.config.debug) ||
          (this.trackingConfig && this.trackingConfig.debug) ||
          (window.PipefyTracking && window.PipefyTracking.debug) ||
          window.PIPEFY_TRACKING_DEBUG ||
          window.pipefyDebug
        );
      } catch (_) {
        return false;
      }
    }

    log(level, ...args) {
      const prefix = '[HubSpotInjector]';
      if (level === 'error') {
        console.error(prefix, ...args);
        return;
      }
      if (!this.isDebugMode() && level !== 'info') {
        // keep info minimal in non-debug environments if needed
        if (level === 'debug') return;
      }
      const fn = level === 'warn' ? console.warn : console.log;
      fn(prefix, ...args);
    }

    // ---------- Session Manager ----------
    checkSessionManager() {
      const trackingReady =
        typeof window.PipefyTracking !== 'undefined' &&
        window.PipefyTracking &&
        typeof window.PipefyTracking.getSnapshot === 'function';

      if (trackingReady) {
        this.sessionAvailable = true;
        this.logger.log('info', 'session: TrackingClient detected');
        return;
      }

      const hasLegacy =
        typeof window.PipefySessionManager !== 'undefined' &&
        window.PipefySessionManager &&
        typeof window.PipefySessionManager.getSessionData === 'function';

      const legacyDisabled =
        typeof window.PipefySessionManager === 'undefined' &&
        typeof window.pipefySessionConfig === 'undefined';

      this.sessionAvailable = hasLegacy;
      this.logger.log('info', 'session: legacy availability', { available: this.sessionAvailable });

      if (hasLegacy) {
        try {
          const preview = window.PipefySessionManager.getSessionData();
          this.logger.log('debug', 'session: legacy data preview keys', Object.keys(preview || {}));
        } catch (e) {
          this.logger.log('warn', 'session: legacy preview failed', e);
        }
        return;
      }

      if (legacyDisabled) {
        this.logger.log('warn', 'session: no tracking manager detected, polling for availability');
      }

      const start = Date.now();
      const id = setInterval(() => {
        const ready =
          (typeof window.PipefyTracking !== 'undefined' &&
            window.PipefyTracking &&
            typeof window.PipefyTracking.getSnapshot === 'function') ||
          (typeof window.PipefySessionManager !== 'undefined' &&
            window.PipefySessionManager &&
            typeof window.PipefySessionManager.getSessionData === 'function');

        if (ready) {
          clearInterval(id);
          this.sessionAvailable = true;
          this.logger.log('info', 'session: manager detected (poll)');
        } else if (Date.now() - start > 10000) {
          clearInterval(id);
          this.logger.log('warn', 'session: timeout waiting for tracking manager');
        }
      }, 250);
      this.pollers.push(id);
    }

    getSessionDataSafe() {
      try {
        if (!this.sessionAvailable) return null;

        if (typeof window.PipefyTracking !== 'undefined' && window.PipefyTracking) {
          const snapshot = window.PipefyTracking.getSnapshot?.() || {};
          const leadData = window.PipefyTracking.getLeadData?.() || {};
          return {
            ...leadData,
            tracking_snapshot: snapshot,
          };
        }

        if (
          typeof window.PipefySessionManager !== 'undefined' &&
          window.PipefySessionManager &&
          typeof window.PipefySessionManager.getSessionData === 'function'
        ) {
          const data = window.PipefySessionManager.getSessionData();
          if (data && typeof data === 'object') return data;
        }
        return null;
      } catch (e) {
        this.logger.log('warn', 'session: getSessionData failed', e);
        return null;
      }
    }

    // ---------- HubSpot Hook & Form Handling ----------
    hookHubSpotCreate() {
      const attach = () => {
        if (!window.hbspt || !window.hbspt.forms || !window.hbspt.forms.create) {
          return false;
        }
        if (this.hookReady) return true;

        const originalCreate = window.hbspt.forms.create;
        const self = this;
        window.hbspt.forms.create = function (options) {
          const originalOnFormReady = options && options.onFormReady;
          options = options || {};

          options.onFormReady = function ($form) {
            try {
              if (typeof originalOnFormReady === 'function') {
                originalOnFormReady.call(this, $form);
              }
            } catch (err) {
              self.log('warn', 'hook: user onFormReady errored', err);
            }

            try {
              const formElement = $form && $form[0] ? $form[0] : null;
              self.log('info', 'form: onFormReady received', {
                hasElement: Boolean(formElement),
              });
              if (formElement) {
                self.injectFromSession(formElement);
                self.applyPhoneMask(formElement);
                self.collapseHiddenHubSpotFields(formElement);
              }
            } catch (err) {
              self.log('error', 'form: onFormReady handling failed', err);
            }
          };

          return originalCreate.call(this, options);
        };

        this.hookReady = true;
        this.logger.log('info', 'hook: hbspt.forms.create attached');
        return true;
      };

      if (!attach()) {
        this.logger.log('warn', 'hook: hbspt not ready, will poll up to 10s');
        const start = Date.now();
        const id = setInterval(() => {
          if (attach()) {
            clearInterval(id);
          } else if (Date.now() - start > 10000) {
            clearInterval(id);
            this.logger.log('warn', 'hook: timeout waiting for hbspt');
          }
        }, 200);
        this.pollers.push(id);
      }
    }

    processExistingForms() {
      try {
        const forms = document.querySelectorAll('.hs-form, [data-hs-form]');
        this.logger.log('info', 'forms: existing count', forms.length);
        forms.forEach(form => {
          this.injectFromSession(form);
          this.applyPhoneMask(form);
          this.collapseHiddenHubSpotFields(form);
        });
      } catch (e) {
        this.logger.log('warn', 'forms: failed scanning existing', e);
      }
    }

    /**
     * Brazilian phone mask — same pattern as Book a Demo form.
     * Format: (11) 99999-9999 / (11) 9999-9999
     */
    formatPhone(raw) {
      const digits = String(raw || '')
        .replace(/\D/g, '')
        .slice(0, 11);
      if (digits.length <= 2) return digits.length ? '(' + digits : '';
      if (digits.length <= 6) return '(' + digits.slice(0, 2) + ') ' + digits.slice(2);
      if (digits.length <= 10) {
        return '(' + digits.slice(0, 2) + ') ' + digits.slice(2, 6) + '-' + digits.slice(6);
      }
      return '(' + digits.slice(0, 2) + ') ' + digits.slice(2, 7) + '-' + digits.slice(7);
    }

    isPhoneInput(input) {
      if (!input || input.tagName !== 'INPUT') return false;
      if (input.type === 'hidden' || input.type === 'submit' || input.type === 'checkbox') {
        return false;
      }
      if (input.type === 'tel') return true;
      const name = String(
        input.name || input.id || input.getAttribute('data-reactid') || ''
      ).toLowerCase();
      const label = (
        input.closest('.hs-form-field')?.querySelector('label')?.textContent || ''
      ).toLowerCase();
      return (
        /phone|mobile|whatsapp|telefone|celular|phonenumber/.test(name) ||
        /phone|telefone|whatsapp|celular/.test(label)
      );
    }

    applyPhoneMask(formElement) {
      try {
        const root =
          formElement ||
          document.querySelector('.pipefy-hubspot-form form') ||
          document.querySelector('form.hs-form');
        if (!root || typeof root.querySelectorAll !== 'function') return;

        const inputs = root.querySelectorAll('input');
        inputs.forEach(input => {
          if (input.closest('pipefy-partners-form')) return;
          if (!this.isPhoneInput(input)) return;
          if (input.dataset.pipefyPhoneMask === '1') {
            // Keep placeholder in BR format even if HubSpot resets it later
            input.placeholder = '(11) 99999-9999';
            return;
          }

          input.dataset.pipefyPhoneMask = '1';
          input.setAttribute('type', 'tel');
          input.setAttribute('inputmode', 'tel');
          input.setAttribute('autocomplete', 'tel');
          input.setAttribute('maxlength', '16');
          input.placeholder = '(11) 99999-9999';

          if (input.value) {
            input.value = this.formatPhone(input.value);
          }

          input.addEventListener('input', event => {
            const target = event.target;
            target.value = this.formatPhone(target.value);
          });
        });
      } catch (err) {
        this.logger.log('warn', 'phone: failed to apply mask', err);
      }
    }

    /**
     * Collapse HubSpot dependent/hidden field wrappers that still occupy layout
     * space (display:none inputs inside fieldsets with margin).
     */
    collapseHiddenHubSpotFields(formElement) {
      try {
        const root =
          formElement ||
          document.querySelector('.pipefy-hubspot-form form') ||
          document.querySelector('form.hs-form');
        if (!root || typeof root.querySelectorAll !== 'function') return;

        const isVisuallyHidden = el => {
          if (!el) return true;
          const style = window.getComputedStyle(el);
          return (
            style.display === 'none' ||
            style.visibility === 'hidden' ||
            style.height === '0px' ||
            el.getAttribute('aria-hidden') === 'true'
          );
        };

        const hasVisibleControl = field => {
          const controls = field.querySelectorAll(
            'input:not([type="hidden"]):not([type="submit"]), textarea, select'
          );
          return Array.from(controls).some(control => !isVisuallyHidden(control));
        };

        root.querySelectorAll('.hs-form-field').forEach(field => {
          if (isVisuallyHidden(field) || !hasVisibleControl(field)) {
            field.style.setProperty('display', 'none', 'important');
            field.style.setProperty('margin', '0', 'important');
            field.style.setProperty('height', '0', 'important');
            field.style.setProperty('padding', '0', 'important');
            field.style.setProperty('overflow', 'hidden', 'important');
          }
        });

        root.querySelectorAll('fieldset').forEach(fieldset => {
          const visibleFields = Array.from(fieldset.querySelectorAll('.hs-form-field')).filter(
            field => !isVisuallyHidden(field) && hasVisibleControl(field)
          );

          const hasLegalOrSubmit = Boolean(
            fieldset.querySelector(
              '.legal-consent-container, .hs_submit, .actions, input[type="submit"]'
            )
          );

          if (visibleFields.length === 0 && !hasLegalOrSubmit) {
            fieldset.style.setProperty('display', 'none', 'important');
            fieldset.style.setProperty('margin', '0', 'important');
            fieldset.style.setProperty('height', '0', 'important');
            fieldset.style.setProperty('padding', '0', 'important');
            fieldset.style.setProperty('overflow', 'hidden', 'important');
          }
        });
      } catch (err) {
        this.logger.log('warn', 'form: failed to collapse hidden fields', err);
      }
    }

    watchPhoneMasks() {
      try {
        if (this._phoneMaskObserver) return;

        const run = () => {
          document
            .querySelectorAll(
              '.pipefy-hubspot-form form, form.hs-form, .pipefy-hubspot-form__embed'
            )
            .forEach(node => {
              this.applyPhoneMask(node);
              this.collapseHiddenHubSpotFields(node);
            });
        };

        run();
        this._phoneMaskObserver = new MutationObserver(() => run());
        this._phoneMaskObserver.observe(document.body, { childList: true, subtree: true });
        [300, 800, 1600].forEach(ms => setTimeout(run, ms));
      } catch (err) {
        this.logger.log('warn', 'phone: failed to watch forms', err);
      }
    }

    // ---------- Injection ----------
    injectFromSession(formElement) {
      try {
        if (!formElement) return false;

        // Only inject into HubSpot forms - verify the form element is a HubSpot form
        const isElementHubSpotForm =
          (formElement.classList && formElement.classList.contains('hs-form')) ||
          (formElement.hasAttribute && formElement.hasAttribute('data-hs-form'));

        // Check if the element is inside a HubSpot form
        const isInsideHubSpotForm =
          (formElement.closest && formElement.closest('.hs-form')) ||
          (formElement.closest && formElement.closest('[data-hs-form]'));

        // If it's an input, make sure it's inside a HubSpot form, not just any form
        const isInput = formElement.tagName === 'INPUT';
        if (isInput) {
          const parentForm = formElement.closest('form');
          if (parentForm) {
            const isParentHubSpotForm =
              (parentForm.classList && parentForm.classList.contains('hs-form')) ||
              (parentForm.hasAttribute && parentForm.hasAttribute('data-hs-form'));
            if (!isParentHubSpotForm) {
              this.logger.log('debug', 'inject: skipping input not in HubSpot form', {
                inputName: formElement.name,
                formClass: parentForm.className,
              });
              return false;
            }
          }
        }

        const isHubSpotForm = isElementHubSpotForm || isInsideHubSpotForm;

        if (!isHubSpotForm) {
          this.logger.log('debug', 'inject: skipping non-HubSpot form', {
            tagName: formElement.tagName,
            className: formElement.className,
            hasDataHsForm: formElement.hasAttribute?.('data-hs-form'),
          });
          return false;
        }

        const sessionData = this.getSessionDataSafe();
        if (!sessionData) {
          this.logger.log('warn', 'inject: session data unavailable, skipping');
          return false;
        }

        // Flatten session leaves so nested objects like utm_parameters/custom_parameters are supported.
        const leafEntries = this.collectLeafEntries(sessionData);
        this.logger.log('debug', 'inject: leaf keys', Array.from(leafEntries.keys()));

        // Keys that should never be injected (e.g., internal tracking keys, search fields, etc.)
        const excludedKeys = new Set([
          'search', // Search form inputs should not be auto-filled
          's', // WordPress search field
          'product_search', // Product search parameter (EN)
          'buscar_produto', // Product search parameter (PT-BR)
          'q', // Generic query field
          'query', // Generic query field
          'tracking_snapshot', // Internal tracking data
        ]);

        // Additional check: search-related field names
        const searchFieldNames = [
          'search',
          's',
          'product_search',
          'buscar_produto',
          'q',
          'query',
          'search_query',
          'search_term',
        ];

        let injectedCount = 0;
        leafEntries.forEach((leafValue, leafKey) => {
          // Skip excluded keys
          if (excludedKeys.has(leafKey)) {
            this.logger.log('debug', 'inject: skipping excluded key', { key: leafKey });
            return;
          }

          // Additional check: skip if the key is a search-related field name
          if (searchFieldNames.includes(leafKey.toLowerCase())) {
            this.logger.log('debug', 'inject: skipping search-related key', { key: leafKey });
            return;
          }

          const selector = `input[name="${CSS.escape(leafKey)}"]`;
          const inputs = formElement.querySelectorAll(selector);
          if (!inputs || inputs.length === 0) return;

          inputs.forEach(input => {
            try {
              // Additional check: skip if the input name is a search-related field
              if (searchFieldNames.includes(input.name?.toLowerCase())) {
                this.logger.log('debug', 'inject: skipping search-related input', {
                  key: leafKey,
                  inputName: input.name,
                });
                return;
              }

              input.value = String(leafValue);
              input.dispatchEvent(new Event('input', { bubbles: true }));
              input.dispatchEvent(new Event('change', { bubbles: true }));
              injectedCount += 1;
            } catch (e) {
              this.logger.log('warn', 'inject: failed setting input value', { key: leafKey, e });
            }
          });
        });

        this.logger.log('info', 'inject: completed', { injectedCount });
        return injectedCount > 0;
      } catch (e) {
        this.logger.log('error', 'inject: unexpected failure', e);
        return false;
      }
    }

    // Traverse session data to collect [key, value] for primitive leaves.
    // For arrays of primitives, joins with comma; arrays of objects are skipped.
    collectLeafEntries(obj) {
      const result = new Map();

      const isPrimitive = v => v === null || ['string', 'number', 'boolean'].includes(typeof v);
      const normalize = v => {
        if (v === null || typeof v === 'undefined') return null;
        if (Array.isArray(v)) {
          // If array of primitives → join, else skip
          const allPrimitives = v.every(
            x => x === null || ['string', 'number', 'boolean'].includes(typeof x)
          );
          return allPrimitives ? v.map(x => (x === null ? '' : String(x))).join(',') : null;
        }
        if (typeof v === 'object') return null;
        return String(v);
      };

      // Helper to detect if a string is a URL
      const isUrl = str => {
        if (typeof str !== 'string') return false;
        try {
          const url = new URL(str);
          return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
          return false;
        }
      };

      // Search-related field names that should never be collected
      const searchFieldNames = [
        'search',
        's',
        'product_search',
        'buscar_produto',
        'q',
        'query',
        'search_query',
        'search_term',
      ];

      const walk = (current, path = '') => {
        try {
          if (!current || typeof current !== 'object') return;
          Object.keys(current).forEach(k => {
            const val = current[k];
            const currentPath = path ? `${path}.${k}` : k;

            if (isPrimitive(val) || Array.isArray(val)) {
              const nv = normalize(val);
              if (nv !== null) {
                // Skip if the value is a URL - URLs should not be processed as leaf entries
                if (isUrl(nv)) {
                  this.logger.log('debug', 'collectLeafEntries: skipping URL value', {
                    key: k,
                    path: currentPath,
                    value: nv.substring(0, 100) + (nv.length > 100 ? '...' : ''),
                  });
                  return;
                }

                // Skip search-related keys
                if (searchFieldNames.includes(k.toLowerCase())) {
                  this.logger.log('debug', 'collectLeafEntries: skipping search-related key', {
                    key: k,
                    path: currentPath,
                    value: nv,
                  });
                  return;
                }

                result.set(k, nv);
              }
              return;
            }
            // nested object → recurse
            if (val && typeof val === 'object') {
              walk(val, currentPath);
            }
          });
        } catch (e) {
          this.logger.log('warn', 'collectLeafEntries: error walking object', { path, e });
        }
      };

      walk(obj);
      return result;
    }

    // ---------- Cleanup ----------
    destroy() {
      try {
        this.pollers.forEach(id => clearInterval(id));
        this.pollers = [];
      } catch (_) {}
      this.logger.log('info', 'destroy: cleaned up');
    }

    // ---------- Debug API ----------
    status() {
      const sessionKeys = (() => {
        try {
          const data = this.getSessionDataSafe();
          return data ? Object.keys(data) : [];
        } catch (_) {
          return [];
        }
      })();

      const formsCount = (() => {
        try {
          return document.querySelectorAll('.hs-form, [data-hs-form]').length;
        } catch (_) {
          return 0;
        }
      })();

      const info = {
        hookReady: this.hookReady,
        sessionAvailable: this.sessionAvailable,
        formsCount: formsCount,
        sessionKeys: sessionKeys,
        startedAt: this.startedAt,
        now: Date.now(),
      };

      this.logger.log('info', 'status', info);
      return info;
    }

    reinject(target) {
      try {
        if (target && target.nodeType === Node.ELEMENT_NODE) {
          this.logger.log('info', 'reinject: on provided element');
          return this.injectFromSession(target);
        }
        this.logger.log('info', 'reinject: scanning existing forms');
        this.processExistingForms();
        return true;
      } catch (e) {
        this.logger.log('error', 'reinject: failed', e);
        return false;
      }
    }

    // ---------- HubSpot postMessage fallback (logging + best-effort) ----------
    setupHubSpotMessageListener() {
      try {
        const handler = event => {
          try {
            const data = event && event.data ? event.data : null;
            if (!data || data.type !== 'hsFormCallback') return;
            if (data.eventName !== 'onFormReady') return;

            this.logger.log('info', 'message: hsFormCallback onFormReady received', {
              origin: event.origin,
              keys: Object.keys(data || {}),
            });

            // Try to identify the iframe that posted the message
            let matchedIframe = null;
            try {
              const iframes = document.querySelectorAll('iframe');
              for (let i = 0; i < iframes.length; i++) {
                const frame = iframes[i];
                if (frame && frame.contentWindow === event.source) {
                  matchedIframe = frame;
                  break;
                }
              }
            } catch (_) {}

            if (matchedIframe) {
              this.logger.log('debug', 'message: matched iframe', {
                id: matchedIframe.id || null,
                src: matchedIframe.src || null,
              });
              // Best-effort: attempt to access form inside iframe if same-origin
              try {
                const doc = matchedIframe.contentDocument || matchedIframe.contentWindow.document;
                const form = doc && doc.querySelector ? doc.querySelector('form') : null;
                if (form) {
                  this.injectFromSession(form);
                  this.applyPhoneMask(form);
                  this.collapseHiddenHubSpotFields(form);
                } else {
                  this.logger.log('warn', 'message: iframe accessible but form not found');
                }
              } catch (accessErr) {
                // Likely cross-origin, injection not possible from parent; rely on onFormReady hook
                this.logger.log(
                  'warn',
                  'message: iframe not accessible (likely cross-origin)',
                  accessErr
                );
              }
            } else {
              // Fallback: re-scan parent DOM for non-iframe embeds
              this.processExistingForms();
            }
          } catch (err) {
            this.logger.log('warn', 'message: handler error', err);
          }
        };

        window.addEventListener('message', handler);
        this._hsMessageHandler = handler;
        this.logger.log('info', 'message: listener attached');
      } catch (e) {
        this.logger.log('warn', 'message: failed to attach listener', e);
      }
    }
  }

  // Initialize when script loads
  if (typeof window !== 'undefined') {
    window.HubSpotInjector = new HubSpotInjector();
  }
})();
